import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  momentLocalizer,
  type Event,
  type SlotInfo,
  type View,
} from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './team-calendar.calendar.css';
import {
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  Pencil,
  Plus,
  Settings,
  Tag,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDiscardGuard } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { usersService, type User } from '@/features/users/services/users.service';
import {
  dailyTasksService,
  type CreateTaskDto,
  type DailyTask,
  type TeamCalendarRecurrencePattern,
} from '@/features/daily-tasks/services/daily-tasks.service';
import {
  taskTagsService,
} from '@/features/daily-tasks/services/task-tags.service';
import { usePermissions } from '@/common/hooks/usePermissions';
import { Permission } from '@/common/enums/permissions.enum';
import { PermissionGate } from '@/components/PermissionGate';
import { TaskDetailModal } from '@/features/daily-tasks/components/TaskDetailModal';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const localizer = momentLocalizer(moment);

const TAG_ICON_OPTIONS = ['📌', '📣', '🔥', '🚀', '✅', '⚠️', '🎯', '📅', '🧠', '🛠️'];
const ALL_TEAMS_OPTION = '__all_teams__';

export const Route = createFileRoute('/_authenticated/team-calendar')({
  validateSearch: (search: Record<string, unknown>) => ({
    openDashboardSettings:
      search.openDashboardSettings === true ||
      search.openDashboardSettings === '1' ||
      search.openDashboardSettings === 'true',
  }),
  component: TeamCalendarPage,
});

function TeamCalendarPage() {
  return (
    <PermissionGate permission={Permission.TEAM_CALENDAR_READ}>
      <TeamCalendarContent />
    </PermissionGate>
  );
}

function TeamCalendarContent() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { openDashboardSettings } = useSearch({
    from: '/_authenticated/team-calendar',
  });
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission(Permission.TEAM_CALENDAR_WRITE);

  const [view, setView] = useState<View>('month');
  const [date, setDate] = useState(new Date());

  const [createOpen, setCreateOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<DailyTask | null>(null);
  const [selectedTask, setSelectedTask] = useState<DailyTask | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'manual' | 'whatsapp' | 'personal'>('manual');
  const [workflowStatus, setWorkflowStatus] = useState<
    'aguardando' | 'em_progresso' | 'bloqueada' | 'suspensa' | 'concluida'
  >('aguardando');
  const [priority, setPriority] = useState<'baixa' | 'media' | 'alta' | 'urgente'>('media');
  const [dueDate, setDueDate] = useState('');
  const [hasEndDate, setHasEndDate] = useState(false);
  const [endDate, setEndDate] = useState('');
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [recurrencePattern, setRecurrencePattern] = useState<TeamCalendarRecurrencePattern | 'none'>('none');

  const [preAlertDays, setPreAlertDays] = useState(5);
  const [notifDays, setNotifDays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  /** Filtro global da coluna Próximos prazos (Minha Dashboard); só editável com permissão de escrita. */
  const [upcomingDeadlinesTaskTagId, setUpcomingDeadlinesTaskTagId] = useState<string | null>(null);

  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#2563EB');
  const [newTagIcon, setNewTagIcon] = useState(TAG_ICON_OPTIONS[0]);
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [editTagName, setEditTagName] = useState('');
  const [editTagColor, setEditTagColor] = useState('#2563EB');
  const [editTagIcon, setEditTagIcon] = useState(TAG_ICON_OPTIONS[0]);

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['team-calendar-tasks'],
    queryFn: () => dailyTasksService.getTeamCalendarTasks(),
  });

  const { data: teams = [] } = useQuery({
    queryKey: ['team-calendar-teams'],
    queryFn: () => dailyTasksService.getTeamCalendarTeams(),
  });

  const { data: users = [] } = useQuery({
    queryKey: ['team-calendar-users'],
    queryFn: async () => {
      const response = await usersService.getAll();
      const list = Array.isArray(response.data) ? response.data : (response.data as any)?.data || [];
      return list.filter((user: User) => user.active);
    },
    enabled: canWrite,
  });

  const { data: allTags = [] } = useQuery({
    queryKey: ['task-tags'],
    queryFn: () => taskTagsService.getAll(),
    enabled: canWrite,
  });

  const { data: settings } = useQuery({
    queryKey: ['team-calendar-settings'],
    queryFn: () => dailyTasksService.getTeamCalendarSettings(),
  });

  useEffect(() => {
    if (settings?.preAlertDays != null) {
      setPreAlertDays(settings.preAlertDays);
    }
    if (settings?.notificationDaysOfWeek != null) {
      setNotifDays(settings.notificationDaysOfWeek);
    }
    if (settings) {
      setUpcomingDeadlinesTaskTagId(settings.upcomingDeadlinesTaskTagId ?? null);
    }
  }, [settings]);

  useEffect(() => {
    if (!openDashboardSettings) return;
    if (canWrite) {
      setSettingsOpen(true);
    }
    navigate({
      to: '/team-calendar',
      search: { openDashboardSettings: false },
      replace: true,
    });
  }, [openDashboardSettings, canWrite, navigate]);

  const createTaskMutation = useMutation({
    mutationFn: (dto: CreateTaskDto) => dailyTasksService.createTask(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-calendar-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['daily-tasks'] });
      setCreateOpen(false);
      resetCreateForm();
    },
  });

  const completeTaskMutation = useMutation({
    mutationFn: (taskId: string) => dailyTasksService.completeTask(taskId, 0),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-calendar-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['daily-tasks'] });
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (taskId: string) => dailyTasksService.deleteTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-calendar-tasks'] });
      setTaskToDelete(null);
    },
  });

  const updateSettingsMutation = useMutation({
    mutationFn: (payload: {
      preAlertDays: number;
      notificationDaysOfWeek: number[];
      upcomingDeadlinesTaskTagId: string | null;
    }) => dailyTasksService.updateTeamCalendarSettings(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-calendar-settings'] });
      setSettingsOpen(false);
    },
  });

  const triggerAlertsMutation = useMutation({
    mutationFn: () => dailyTasksService.triggerTeamCalendarAlerts(),
  });

  const createTagMutation = useMutation({
    mutationFn: () =>
      taskTagsService.create({
        name: newTagName.trim(),
        color: newTagColor,
        icon: newTagIcon,
      }),
    onSuccess: () => {
      setNewTagName('');
      setNewTagColor('#2563EB');
      setNewTagIcon(TAG_ICON_OPTIONS[0]);
      queryClient.invalidateQueries({ queryKey: ['task-tags'] });
      queryClient.invalidateQueries({ queryKey: ['team-calendar-tasks'] });
    },
  });

  const deleteTagMutation = useMutation({
    mutationFn: (tagId: string) => taskTagsService.remove(tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-tags'] });
      queryClient.invalidateQueries({ queryKey: ['team-calendar-tasks'] });
    },
  });

  const updateTagMutation = useMutation({
    mutationFn: (payload: { id: string; name: string; color: string; icon: string }) =>
      taskTagsService.update(payload.id, {
        name: payload.name,
        color: payload.color,
        icon: payload.icon,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-tags'] });
      queryClient.invalidateQueries({ queryKey: ['team-calendar-tasks'] });
      setEditingTagId(null);
      setEditTagName('');
      setEditTagColor('#2563EB');
      setEditTagIcon(TAG_ICON_OPTIONS[0]);
    },
  });

  const createDirty =
    !!(title.trim() || description.trim()) ||
    assigneeIds.length > 0 ||
    teamIds.length > 0 ||
    tagIds.length > 0;
  const onCreateOpenChange = useDiscardGuard(createDirty, (open) => {
    setCreateOpen(open);
    if (!open) resetCreateForm();
  });

  const resetCreateForm = () => {
    setTitle('');
    setDescription('');
    setType('manual');
    setWorkflowStatus('aguardando');
    setPriority('media');
    setDueDate('');
    setHasEndDate(false);
    setEndDate('');
    setAssigneeIds([]);
    setTeamIds([]);
    setTagIds([]);
    setRecurrencePattern('none');
  };

  const handleCreateTask = () => {
    if (!title.trim()) return;
    if (assigneeIds.length === 0) return;
    if (hasEndDate && endDate && dueDate && new Date(endDate).getTime() < new Date(dueDate).getTime()) {
      toast.error('A data final não pode ser menor que a data inicial.');
      return;
    }

    const dto: CreateTaskDto = {
      type,
      title: title.trim(),
      description: description.trim() || undefined,
      workflowStatus,
      priority,
      dueDate: dueDate ? new Date(dueDate).toISOString() : undefined,
      endDate: hasEndDate && endDate ? new Date(endDate).toISOString() : null,
      assigneeIds,
      teamIds,
      tagIds,
      creationContext: 'team-calendar',
      isTeamCalendar: true,
      recurrencePattern: recurrencePattern === 'none' ? undefined : recurrencePattern,
    };

    createTaskMutation.mutate(dto);
  };

  const handleSelectSlot = (slot: SlotInfo) => {
    if (!canWrite) return;
    const chosenDate = Array.isArray(slot.slots) && slot.slots.length > 0 ? slot.slots[0] : slot.start;
    const dateWithDefaultHour = new Date(chosenDate);
    dateWithDefaultHour.setHours(9, 0, 0, 0);

    const year = dateWithDefaultHour.getFullYear();
    const month = String(dateWithDefaultHour.getMonth() + 1).padStart(2, '0');
    const day = String(dateWithDefaultHour.getDate()).padStart(2, '0');
    const hour = String(dateWithDefaultHour.getHours()).padStart(2, '0');
    const minute = String(dateWithDefaultHour.getMinutes()).padStart(2, '0');
    const endDateWithDefaultHour = new Date(dateWithDefaultHour);
    endDateWithDefaultHour.setHours(endDateWithDefaultHour.getHours() + 1);
    const endYear = endDateWithDefaultHour.getFullYear();
    const endMonth = String(endDateWithDefaultHour.getMonth() + 1).padStart(2, '0');
    const endDay = String(endDateWithDefaultHour.getDate()).padStart(2, '0');
    const endHour = String(endDateWithDefaultHour.getHours()).padStart(2, '0');
    const endMinute = String(endDateWithDefaultHour.getMinutes()).padStart(2, '0');

    setDueDate(`${year}-${month}-${day}T${hour}:${minute}`);
    setEndDate(`${endYear}-${endMonth}-${endDay}T${endHour}:${endMinute}`);
    setCreateOpen(true);
  };

  const taskEvents = useMemo<Event[]>(
    () =>
      tasks
        .filter((task) => !!task.dueDate)
        .map((task) => ({
          id: task.id,
          title: task.title,
          start: new Date(task.dueDate!),
          end: task.endDate ? new Date(task.endDate) : new Date(task.dueDate!),
          allDay: false,
          resource: task,
        })),
    [tasks],
  );

  const selectedDateWeekRange = useMemo(() => {
    const reference = new Date(date);
    const day = reference.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const start = new Date(reference);
    start.setDate(reference.getDate() + diffToMonday);
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + 7);

    return { start, end };
  }, [date]);

  const weeklyAgenda = useMemo(
    () =>
      tasks
        .filter((task) => task.dueDate)
        .filter((task) => {
          const dueDateObj = new Date(task.dueDate!);
          return (
            dueDateObj >= selectedDateWeekRange.start &&
            dueDateObj < selectedDateWeekRange.end
          );
        })
        .sort((a, b) => {
          const dueA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
          const dueB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
          return dueA - dueB;
        }),
    [tasks, selectedDateWeekRange],
  );

  /** Segunda 00:00 até domingo (fim do intervalo exibido da agenda semanal). */
  const weeklyAgendaRange = useMemo(() => {
    const start = new Date(selectedDateWeekRange.start);
    const endInclusive = new Date(selectedDateWeekRange.start);
    endInclusive.setDate(endInclusive.getDate() + 6);
    endInclusive.setHours(23, 59, 59, 999);
    const dateOpts: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    };
    return {
      start,
      endInclusive,
      startLabel: start.toLocaleDateString('pt-BR', dateOpts),
      endLabel: endInclusive.toLocaleDateString('pt-BR', dateOpts),
    };
  }, [selectedDateWeekRange]);

  const teamUsersPreview = useMemo(() => {
    const selectedTeams = teams.filter((team) => teamIds.includes(team.id));
    const members = selectedTeams
      .flatMap((team) => team.users || [])
      .filter((member) => member.active !== false);
    return Array.from(
      new Map(members.map((member) => [member.id, member])).values(),
    );
  }, [teams, teamIds]);

  const getEventClassName = (task: DailyTask) => {
    const primaryTag = task.tags?.[0];
    const color = primaryTag?.color;

    if (task.isVirtual) {
      return 'bg-slate-500 text-white border-slate-600 opacity-80';
    }

    if (task.isRecurrenceSuperseded || task.recurrenceGeneratedAt) {
      return 'bg-slate-400 text-white border-slate-500 opacity-70';
    }

    if (task.status === 'done') {
      return 'bg-emerald-600 text-white border-emerald-600';
    }

    if (color) {
      return 'text-white border-transparent';
    }

    return 'bg-sky-600 text-white border-sky-600';
  };

  const getUserDisplayName = (user: User) => {
    if (user.fullName) {
      return `${user.fullName.split(' ')[0]} - ${user.email}`;
    }
    return user.email;
  };

  const handleTaskOpen = (task: DailyTask) => {
    if (task.isVirtual) {
      toast.info('Esta tarefa recorrente ainda não foi criada.');
      return;
    }
    if (task.isRecurrenceSuperseded || task.recurrenceGeneratedAt) {
      toast.info('Esta ocorrência já foi substituída por uma nova recorrência.');
      return;
    }
    setSelectedTask(task);
  };

  const startTagEdit = (tag: { id: string; name: string; color: string; icon?: string | null }) => {
    setEditingTagId(tag.id);
    setEditTagName(tag.name);
    setEditTagColor(tag.color || '#2563EB');
    setEditTagIcon(tag.icon || TAG_ICON_OPTIONS[0]);
  };

  const cancelTagEdit = () => {
    setEditingTagId(null);
    setEditTagName('');
    setEditTagColor('#2563EB');
    setEditTagIcon(TAG_ICON_OPTIONS[0]);
  };

  const showWeeklyAgendaPanel = view !== 'agenda';
  const isMonthView = view === 'month';

  return (
    <TooltipProvider>
      <div className="flex w-full flex-col p-4 md:p-6 pt-6 md:pt-7 bg-gradient-to-b from-muted/20 to-background">
      <Card className="mb-4 shrink-0 shadow-sm border-slate-200/60">
        <CardContent className="py-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" asChild>
              <Link to="/my-dashboard">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Calendário da equipe</h1>
              <p className="text-sm text-muted-foreground">
                Planeje atividades da equipe com recorrência, tags e alertas prévios.
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                <Badge variant="outline" className="gap-1">
                  <CalendarClock className="h-3.5 w-3.5 text-sky-600" /> Agenda da semana
                </Badge>
                <Badge variant="outline" className="gap-1">
                  <Tag className="h-3.5 w-3.5 text-indigo-600" /> Tags com ícone/cor
                </Badge>
                <Badge variant="outline" className="gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Conclusão por escrita
                </Badge>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {canWrite && (
              <>
                <Button variant="outline" size="sm" onClick={() => setTagsOpen(true)}>
                  <Tag className="h-4 w-4 mr-2" />
                  Gerenciar tags
                </Button>
                <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
                  <Settings className="h-4 w-4 mr-2" />
                  Configurações
                </Button>
                <Button size="sm" onClick={() => setCreateOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nova tarefa
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex w-full flex-col gap-4 pt-4">
        <Card className="w-full shadow-sm border-slate-200/60">
          <CardContent className="p-3 md:p-4">
            {isLoading ? (
              <div className="flex min-h-[240px] items-center justify-center text-muted-foreground">
                Carregando tarefas do calendário da equipe...
              </div>
            ) : (
              <div
                className={cn(
                  'w-full rounded-lg border bg-card p-2',
                  isMonthView && 'overflow-visible',
                  !isMonthView && view === 'agenda' && 'min-h-[320px] overflow-visible',
                  !isMonthView &&
                    view !== 'agenda' &&
                    'min-h-[520px] h-[min(75dvh,56rem)] overflow-hidden',
                )}
              >
                <Calendar
                  className={cn('team-calendar-rbc', isMonthView && 'team-calendar-month')}
                  localizer={localizer}
                  events={taskEvents}
                  startAccessor="start"
                  endAccessor="end"
                  view={view}
                  onView={setView}
                  date={date}
                  onNavigate={setDate}
                  selectable={canWrite}
                  popup
                  onSelectSlot={handleSelectSlot}
                  onSelectEvent={(event) => handleTaskOpen(event.resource as DailyTask)}
                  style={
                    isMonthView
                      ? { height: 'auto' }
                      : view === 'agenda'
                        ? { height: 'auto', minHeight: 320 }
                        : { height: '100%' }
                  }
                  messages={{
                    next: 'Próximo',
                    previous: 'Anterior',
                    today: 'Hoje',
                    month: 'Mês',
                    week: 'Semana',
                    day: 'Dia',
                    agenda: 'Agenda',
                  }}
                  components={{
                    event: ({ event }) => {
                      const task = event.resource as DailyTask;
                      const firstTag = task.tags?.[0];
                      return (
                        <div className="group flex items-start gap-1 min-w-0 px-1 py-0.5">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex-1 min-w-0 text-left leading-snug break-words [overflow-wrap:anywhere]">
                                {firstTag?.icon ? `${firstTag.icon} ` : ''}
                                {String(event.title)}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">{String(event.title)}</TooltipContent>
                          </Tooltip>
                          {canWrite && !task.isVirtual && !task.isRecurrenceSuperseded && !task.recurrenceGeneratedAt && (
                            <button
                              type="button"
                              className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity rounded p-0.5 hover:bg-black/10"
                              onClick={(clickEvent) => {
                                clickEvent.preventDefault();
                                clickEvent.stopPropagation();
                                setTaskToDelete(task);
                              }}
                              title="Excluir tarefa"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      );
                    },
                  }}
                  eventPropGetter={(event) => {
                    const task = event.resource as DailyTask;
                    const className = getEventClassName(task);
                    const style = task.tags?.[0]?.color
                      ? {
                          backgroundColor: task.tags[0].color,
                          borderColor: task.tags[0].color,
                        }
                      : undefined;
                    return {
                      className: cn(className, 'rounded border'),
                      style,
                    };
                  }}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {showWeeklyAgendaPanel && (
        <Card className="w-full shrink-0 shadow-sm border-slate-200/60">
          <CardContent className="space-y-3 p-4">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-semibold">Agenda semanal</h2>
              <Badge variant="secondary">{weeklyAgenda.length}</Badge>
            </div>
            <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              <p className="font-medium text-foreground">Período da agenda</p>
              <p className="mt-1">
                <span className="text-foreground/80">Início: </span>
                {weeklyAgendaRange.startLabel}
              </p>
              <p className="mt-0.5">
                <span className="text-foreground/80">Término: </span>
                {weeklyAgendaRange.endLabel}
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              Atividades com data prevista na semana do calendário (segunda a domingo).
            </p>
            <div className="space-y-2">
              {weeklyAgenda.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhuma atividade nesta semana.</p>
              ) : (
                weeklyAgenda.map((task) => (
                  <div
                    key={task.id}
                    className="w-full p-2 rounded-md border hover:bg-muted/70 transition-colors cursor-pointer"
                    onClick={() => handleTaskOpen(task)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleTaskOpen(task);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <p className="text-sm font-medium truncate" title={task.title}>
                              {task.tags?.[0]?.icon ? `${task.tags[0].icon} ` : ''}
                              {task.title}
                            </p>
                          </TooltipTrigger>
                          <TooltipContent>{task.title}</TooltipContent>
                        </Tooltip>
                        <p className="text-xs text-muted-foreground">
                          {task.dueDate
                            ? task.endDate
                              ? `${new Date(task.dueDate).toLocaleString('pt-BR')} — ${new Date(task.endDate).toLocaleString('pt-BR')}`
                              : new Date(task.dueDate).toLocaleString('pt-BR')
                            : 'Sem data'}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(task.tags || []).map((tag) => (
                            <Badge
                              key={tag.id}
                              variant="outline"
                              className="text-[10px]"
                              style={{ borderColor: tag.color }}
                            >
                              {tag.icon ? `${tag.icon} ` : ''}
                              {tag.name}
                            </Badge>
                          ))}
                          {task.isVirtual && (
                            <Badge variant="secondary" className="text-[10px]">Recorrência (fictícia)</Badge>
                          )}
                          {(task.isRecurrenceSuperseded || task.recurrenceGeneratedAt) && (
                            <Badge variant="outline" className="text-[10px]">Finalizada</Badge>
                          )}
                        </div>
                      </div>

                      {canWrite && !task.isVirtual && !task.isRecurrenceSuperseded && !task.recurrenceGeneratedAt && (
                        <div className="flex items-center gap-1">
                          {task.status !== 'done' && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-emerald-600 hover:text-emerald-700"
                              onClick={(event) => {
                                event.stopPropagation();
                                completeTaskMutation.mutate(task.id);
                              }}
                              disabled={completeTaskMutation.isPending}
                              title="Concluir tarefa"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={(event) => {
                              event.stopPropagation();
                              setTaskToDelete(task);
                            }}
                            title="Excluir tarefa"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={onCreateOpenChange}>
        <DialogContent size="lg" busy={createTaskMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Criar atividade do calendário da equipe</DialogTitle>
            <DialogDescription>
              Esta tarefa será marcada como Calendário da equipe e poderá ser recorrente.
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              handleCreateTask();
            }}
          >
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="task-title">Título *</Label>
              <Input
                id="task-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Ex: Reunião de alinhamento mensal"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="task-description">Descrição</Label>
              <Textarea
                id="task-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                rows={3}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={type} onValueChange={(value) => setType(value as any)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="personal">Pessoal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Recorrência</Label>
                <Select
                  value={recurrencePattern}
                  onValueChange={(value) => setRecurrencePattern(value as any)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem recorrência</SelectItem>
                    <SelectItem value="daily">Diária</SelectItem>
                    <SelectItem value="weekly">Semanal</SelectItem>
                    <SelectItem value="biweekly">A cada 2 semanas</SelectItem>
                    <SelectItem value="monthly">Mensal</SelectItem>
                    <SelectItem value="monthly_weekday_1st">Primeira ocorrência do dia da semana no mês</SelectItem>
                    <SelectItem value="quarterly">Trimestral</SelectItem>
                    <SelectItem value="yearly">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={workflowStatus} onValueChange={(value) => setWorkflowStatus(value as any)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aguardando">Aguardando</SelectItem>
                    <SelectItem value="em_progresso">Em progresso</SelectItem>
                    <SelectItem value="bloqueada">Bloqueada</SelectItem>
                    <SelectItem value="suspensa">Suspensa</SelectItem>
                    <SelectItem value="concluida">Concluída</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Prioridade</Label>
                <Select value={priority} onValueChange={(value) => setPriority(value as any)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baixa">Baixa</SelectItem>
                    <SelectItem value="media">Média</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="task-due-date">Data prevista</Label>
                <Input
                  id="task-due-date"
                  type="datetime-local"
                  value={dueDate}
                  onChange={(event) => setDueDate(event.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={hasEndDate}
                    onChange={(event) => setHasEndDate(event.target.checked)}
                  />
                  Possui data final
                </Label>
                {hasEndDate && (
                  <Input
                    id="task-end-date"
                    aria-label="Data final"
                    type="datetime-local"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                  />
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Responsáveis</Label>
              <Select
                value=""
                onValueChange={(value) => {
                  if (value && !assigneeIds.includes(value)) {
                    setAssigneeIds((prev) => [...prev, value]);
                  }
                }}
              >
                <SelectTrigger><SelectValue placeholder="Adicionar responsável" /></SelectTrigger>
                <SelectContent>
                  {users
                    .filter((user: User) => !assigneeIds.includes(user.id))
                    .map((user: User) => (
                      <SelectItem key={user.id} value={user.id}>
                        {getUserDisplayName(user)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <div className="flex flex-wrap gap-1">
                {assigneeIds.map((assigneeId) => {
                  const user = users.find((item: User) => item.id === assigneeId);
                  return (
                    <Badge key={assigneeId} variant="secondary" className="gap-2">
                      {user ? getUserDisplayName(user) : assigneeId}
                      <button
                        type="button"
                        aria-label="Remover"
                        onClick={() => setAssigneeIds((prev) => prev.filter((id) => id !== assigneeId))}
                      >
                        ×
                      </button>
                    </Badge>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Times (múltiplos)</Label>
              <Select
                value=""
                onValueChange={(value) => {
                  if (value === ALL_TEAMS_OPTION) {
                    setTeamIds(teams.map((team) => team.id));
                    return;
                  }
                  if (value && !teamIds.includes(value)) {
                    setTeamIds((prev) => [...prev, value]);
                  }
                }}
              >
                <SelectTrigger><SelectValue placeholder="Adicionar time" /></SelectTrigger>
                <SelectContent>
                  {teams.length > 0 && teamIds.length < teams.length && (
                    <SelectItem value={ALL_TEAMS_OPTION}>Todos</SelectItem>
                  )}
                  {teams
                    .filter((team) => !teamIds.includes(team.id))
                    .map((team) => (
                      <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>

              <div className="flex flex-wrap gap-1">
                {teamIds.map((teamId) => {
                  const team = teams.find((item) => item.id === teamId);
                  return (
                    <Badge key={teamId} variant="outline" className="gap-2">
                      {team?.name || teamId}
                      <button
                        type="button"
                        aria-label="Remover"
                        onClick={() => setTeamIds((prev) => prev.filter((id) => id !== teamId))}
                      >
                        ×
                      </button>
                    </Badge>
                  );
                })}
              </div>

              {teamUsersPreview.length > 0 && (
                <div className="rounded border p-2 bg-muted/30">
                  <p className="text-xs text-muted-foreground mb-1">Membros dos times marcados:</p>
                  <div className="flex flex-wrap gap-1">
                    {teamUsersPreview.map((member) => (
                      <Badge key={member.id} variant="secondary" className="text-[10px]">
                        {member.fullName || member.email}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <Select
                value=""
                onValueChange={(value) => {
                  if (value && !tagIds.includes(value)) {
                    setTagIds((prev) => [...prev, value]);
                  }
                }}
              >
                <SelectTrigger><SelectValue placeholder="Adicionar tag" /></SelectTrigger>
                <SelectContent>
                  {allTags
                    .filter((tag) => !tagIds.includes(tag.id))
                    .map((tag) => (
                      <SelectItem key={tag.id} value={tag.id}>
                        {tag.icon ? `${tag.icon} ` : ''}{tag.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>

              <div className="flex flex-wrap gap-1">
                {tagIds.map((tagId) => {
                  const tag = allTags.find((item) => item.id === tagId);
                  return (
                    <Badge
                      key={tagId}
                      variant="outline"
                      className="gap-2"
                      style={{ borderColor: tag?.color }}
                    >
                      {tag?.icon ? `${tag.icon} ` : ''}
                      {tag?.name || tagId}
                      <button
                        type="button"
                        aria-label="Remover"
                        onClick={() => setTagIds((prev) => prev.filter((id) => id !== tagId))}
                      >
                        ×
                      </button>
                    </Badge>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onCreateOpenChange(false)}>Cancelar</Button>
            <Button
              type="submit"
              loading={createTaskMutation.isPending}
              disabled={assigneeIds.length === 0 || !title.trim()}
            >
              Criar atividade
            </Button>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent size="md" busy={updateSettingsMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Configurações do calendário da equipe</DialogTitle>
            <DialogDescription>
              Alertas por e-mail/notificação e filtro da coluna &quot;Próximos prazos&quot; na Minha Dashboard (visível para todos os usuários com acesso ao calendário).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2 rounded-md border p-3 bg-muted/20">
                <Label htmlFor="upcoming-deadlines-tag">Coluna Próximos prazos (Minha Dashboard)</Label>
                <p className="text-xs text-muted-foreground">
                  Mostrar apenas tarefas com uma tag específica, ou todos os prazos. Apenas quem pode editar o calendário da equipe altera esta opção para toda a organização.
                </p>
                <Select
                  value={upcomingDeadlinesTaskTagId ?? '__all__'}
                  onValueChange={(v) =>
                    setUpcomingDeadlinesTaskTagId(v === '__all__' ? null : v)
                  }
                >
                  <SelectTrigger id="upcoming-deadlines-tag">
                    <SelectValue placeholder="Escolha o filtro" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos os prazos</SelectItem>
                    {allTags.map((tag) => (
                      <SelectItem key={tag.id} value={tag.id}>
                        <span className="flex items-center gap-2">
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                            style={{ backgroundColor: tag.color }}
                          />
                          <span>
                            {tag.icon ? `${tag.icon} ` : ''}
                            {tag.name}
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pre-alert-days">Dias de antecedência</Label>
              <Input
                id="pre-alert-days"
                type="number"
                min={1}
                max={60}
                value={preAlertDays}
                onChange={(event) => setPreAlertDays(Number(event.target.value || 5))}
              />
            </div>
            <div className="space-y-2">
              <Label>Notificar nos dias</Label>
              <p className="text-xs text-muted-foreground">
                Dias desmarcados não receberão notificações do calendário da equipe.
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                {(['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const).map((label, idx) => {
                  const checked = notifDays.includes(idx);
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        setNotifDays((prev) =>
                          checked ? prev.filter((d) => d !== idx) : [...prev, idx].sort((a, b) => a - b),
                        );
                      }}
                      className={[
                        'w-10 h-10 rounded-md text-xs font-medium border transition-colors',
                        checked
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-muted text-muted-foreground border-border hover:bg-muted/70',
                      ].join(' ')}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="rounded-md border border-dashed p-3 space-y-2">
              <p className="text-sm font-medium">Disparar alertas agora</p>
              <p className="text-xs text-muted-foreground">
                Executa imediatamente a mesma verificação da rotina diária e envia notificações para os eventos dentro da janela configurada.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => triggerAlertsMutation.mutate()}
                disabled={triggerAlertsMutation.isPending}
              >
                {triggerAlertsMutation.isPending
                  ? 'Enviando...'
                  : triggerAlertsMutation.isSuccess
                    ? `Concluído — ${triggerAlertsMutation.data?.notifications ?? 0} notificação(ões) enviada(s)`
                    : 'Disparar alertas agora'}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancelar</Button>
            <Button
              onClick={() =>
                updateSettingsMutation.mutate({
                  preAlertDays,
                  notificationDaysOfWeek: notifDays,
                  upcomingDeadlinesTaskTagId,
                })
              }
              loading={updateSettingsMutation.isPending}
            >
              Salvar configurações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={tagsOpen}
        onOpenChange={(open) => {
          setTagsOpen(open);
          if (!open) {
            cancelTagEdit();
          }
        }}
      >
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Tags do calendário da equipe</DialogTitle>
            <DialogDescription>
              Crie, edite e remova tags com cor e ícone para identificar visualmente as atividades.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2 rounded-md border p-3 bg-muted/20">
              <p className="text-sm font-medium">Criar nova tag</p>
              <Label>Nome da tag</Label>
              <Input
                value={newTagName}
                onChange={(event) => setNewTagName(event.target.value)}
                placeholder="Ex: Lançamento"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Cor</Label>
                <Input type="color" value={newTagColor} onChange={(event) => setNewTagColor(event.target.value)} />
              </div>

              <div className="space-y-2">
                <Label>Ícone</Label>
                <Select value={newTagIcon} onValueChange={setNewTagIcon}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TAG_ICON_OPTIONS.map((icon) => (
                      <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              onClick={() => createTagMutation.mutate()}
              disabled={!newTagName.trim() || createTagMutation.isPending}
            >
              {createTagMutation.isPending ? 'Criando...' : 'Criar tag'}
            </Button>

            <div className="space-y-2 max-h-64 overflow-auto border rounded-md p-2 bg-background/60">
              <p className="text-sm font-medium px-1">Tags existentes</p>
              {allTags.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhuma tag cadastrada.</p>
              ) : (
                allTags.map((tag) => (
                  <div key={tag.id} className="p-2 border rounded space-y-2">
                    {editingTagId === tag.id ? (
                      <>
                        <Input
                          value={editTagName}
                          onChange={(event) => setEditTagName(event.target.value)}
                          placeholder="Nome da tag"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="color"
                            value={editTagColor}
                            onChange={(event) => setEditTagColor(event.target.value)}
                          />
                          <Select value={editTagIcon} onValueChange={setEditTagIcon}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {TAG_ICON_OPTIONS.map((icon) => (
                                <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={cancelTagEdit}>
                            Cancelar
                          </Button>
                          <Button
                            size="sm"
                            onClick={() =>
                              updateTagMutation.mutate({
                                id: tag.id,
                                name: editTagName.trim(),
                                color: editTagColor,
                                icon: editTagIcon,
                              })
                            }
                            disabled={!editTagName.trim() || updateTagMutation.isPending}
                          >
                            {updateTagMutation.isPending ? 'Salvando...' : 'Salvar'}
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center justify-between gap-2">
                        <Badge variant="outline" style={{ borderColor: tag.color }}>
                          {tag.icon ? `${tag.icon} ` : ''}
                          {tag.name}
                        </Badge>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startTagEdit(tag)}
                            disabled={deleteTagMutation.isPending || updateTagMutation.isPending}
                          >
                            <Pencil className="h-3.5 w-3.5 mr-1" />
                            Editar
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteTagMutation.mutate(tag.id)}
                            disabled={deleteTagMutation.isPending || updateTagMutation.isPending}
                          >
                            Remover
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!taskToDelete} onOpenChange={() => setTaskToDelete(null)}>
        <DialogContent size="sm" busy={deleteTaskMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Excluir tarefa</DialogTitle>
            <DialogDescription>
              {`Tem certeza que deseja excluir "${taskToDelete?.title || 'esta tarefa'}"?`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskToDelete(null)}>Cancelar</Button>
            <Button
              variant="destructive"
              onClick={() => taskToDelete && deleteTaskMutation.mutate(taskToDelete.id)}
              loading={deleteTaskMutation.isPending}
            >
              Excluir tarefa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TaskDetailModal
        open={!!selectedTask}
        onClose={() => setSelectedTask(null)}
        task={selectedTask}
        creationContext="admin-board"
        readOnly={!canWrite}
        allowCommentsInReadOnly
      />
      </div>
    </TooltipProvider>
  );
}
